import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { GraphQlService } from '@shared/services/graphql-service';
import { ApolloNamespace } from '@shared/enums/apollo-namespaces';
import { TANKER_LINEUP_REPORT_QUERY } from '../graphql/tankers-line-up.graphql';
import { BerthLineUpGroup, TankerLineUpItem, TankerLineUpReport } from '../models/tanker-line-up.model';
import * as TankersLineUpActions from './tankers-line-up.actions';

@Injectable()
export class TankersLineUpEffects {
  private readonly actions$ = inject(Actions);
  private readonly graphqlService = inject(GraphQlService);

  loadReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TankersLineUpActions.loadTankerLineupReport),
      mergeMap(({ portUuid, targetDate }) =>
        this.graphqlService
          .query<any>({
            query: TANKER_LINEUP_REPORT_QUERY,
            apolloNamespace: ApolloNamespace.registration,
            variables: { portUuid }
          })
          .pipe(
            map((response) => {
              const apiVessels = response.data?.tankerLineupReport?.data ?? [];
              const grouped = new Map<string, { portName: string; vessels: TankerLineUpItem[] }>();

              apiVessels.forEach((v: any) => {
                const groupUuid = v.portUuid || 'Unknown';
                const groupName = v.portName || 'Unknown Berth';
                if (!grouped.has(groupUuid)) {
                  grouped.set(groupUuid, { portName: groupName, vessels: [] });
                }
                grouped.get(groupUuid)!.vessels.push({
                  sn: 0,
                  vesselName: v.vesselName || '---',
                  tenderNo: v.tenderNo || '---',
                  eta: v.eta || '---',
                  isArrived: (v.eta ?? '').toUpperCase() === 'ARRIVED',
                  nor: v.nor || 'n/a',
                  etb: v.etb || '---',
                  etd: v.etd || '---',
                  product: v.productName || '---',
                  qty: v.quantity || 0,
                  supplier: v.supplierName || '---'
                });
              });

              const berths: BerthLineUpGroup[] = Array.from(grouped.entries()).map(([uuid, groupInfo], index) => {
                groupInfo.vessels.forEach((v, idx) => {
                  v.sn = idx + 1;
                });
                return {
                  berthCode: `BERTH_${index}`,
                  berthName: groupInfo.portName,
                  location: '',
                  vessels: groupInfo.vessels
                };
              });

              const totalVessels = berths.reduce((sum, b) => sum + b.vessels.length, 0);
              const totalQuantity = berths.reduce(
                (sum, b) => sum + b.vessels.reduce((vSum, v) => vSum + (v.qty || 0), 0),
                0,
              );
              const arrivedCount = berths.reduce(
                (sum, b) => sum + b.vessels.filter((v) => v.isArrived).length,
                0,
              );
              const scheduledCount = totalVessels - arrivedCount;

              const report: TankerLineUpReport = {
                reportTitle: `TANKERS LINE UP ${targetDate}`,
                reportDate: targetDate,
                agencyName: 'PBPA',
                berths,
                totalVessels,
                totalQuantity,
                arrivedCount,
                scheduledCount
              };

              return TankersLineUpActions.loadTankerLineupReportSuccess({ report });
            }),
            catchError((error) => of(TankersLineUpActions.loadTankerLineupReportFailure({ error })))
          )
      )
    )
  );
}
